import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import api from '../lib/api';
import { 
  Link2, 
  Unlink, 
  RefreshCw, 
  ExternalLink, 
  CheckCircle2, 
  AlertCircle, 
  Clock,
  Instagram,
  Facebook,
  Youtube,
  Linkedin,
  Share2,
  Sparkles,
  Users,
  Image,
  Video
} from 'lucide-react';

// Platform configuration with icons and colors
const PLATFORMS = {
  meta: {
    name: 'Meta (Instagram & Facebook)',
    description: 'Connect your Instagram Business account and Facebook Pages to auto-publish products and content.',
    icon: Instagram,
    secondaryIcon: Facebook,
    color: 'bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500',
    textColor: 'text-pink-600',
    features: ['Instagram Posts', 'Facebook Page Posts', 'Stories', 'Reels'],
  },
  youtube: {
    name: 'YouTube',
    description: 'Upload product videos and tutorials directly to your YouTube channel.',
    icon: Youtube,
    color: 'bg-red-600',
    textColor: 'text-red-600',
    features: ['Video Uploads', 'Shorts', 'Community Posts'],
  },
  tiktok: {
    name: 'TikTok',
    description: 'Share short-form videos and product showcases on TikTok.',
    icon: Video,
    color: 'bg-black',
    textColor: 'text-gray-900',
    features: ['Video Posts', 'Product Showcases'],
  },
  linkedin: {
    name: 'LinkedIn',
    description: 'Share professional content and product announcements with your network.',
    icon: Linkedin,
    color: 'bg-blue-700',
    textColor: 'text-blue-700',
    features: ['Text Posts', 'Articles', 'Product Updates'],
  },
  pinterest: {
    name: 'Pinterest',
    description: 'Create pins for your products to drive traffic from visual search.',
    icon: Image,
    color: 'bg-red-700',
    textColor: 'text-red-700',
    features: ['Pins', 'Product Pins', 'Boards'],
  },
};

export default function SocialConnectionsPage() {
  const { user } = useAuth();
  const [accounts, setAccounts] = useState({});
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(null);
  const [refreshing, setRefreshing] = useState(null);

  // Load connected accounts
  const loadAccounts = useCallback(async () => {
    if (!user?.id) return;
    try {
      const [accountsRes, platformsRes] = await Promise.all([
        api.get(`/social/accounts/${user.id}`),
        api.get('/social/platforms'),
      ]);
      
      const accountsMap = {};
      accountsRes.data.accounts.forEach(acc => {
        accountsMap[acc.provider] = acc;
      });
      setAccounts(accountsMap);
      setPlatforms(platformsRes.data.platforms);
    } catch (err) {
      console.error('Failed to load accounts:', err);
      toast.error('Failed to load connected accounts');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  // Handle OAuth callback message from popup
  useEffect(() => {
    const handleMessage = async (event) => {
      if (event.origin !== window.location.origin) return;
      if (event.data.type === 'oauth_callback') {
        const { provider, code, error, state } = event.data;
        
        if (error) {
          toast.error(`Connection failed: ${error}`);
          setConnecting(null);
          return;
        }
        
        if (code) {
          try {
            const res = await api.post(`/social/auth/${provider}/callback`, {
              code,
              state,
              user_id: user.id,
            });
            
            if (res.data.ok) {
              toast.success(`${PLATFORMS[provider]?.name || provider} connected successfully!`);
              loadAccounts();
            } else {
              toast.error(res.data.error || 'Connection failed');
            }
          } catch (err) {
            toast.error(err.response?.data?.detail || 'Connection failed');
          }
        }
        setConnecting(null);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [user?.id, loadAccounts]);

  // Initiate OAuth connection
  const handleConnect = async (provider) => {
    setConnecting(provider);
    try {
      const res = await api.get(`/social/auth/${provider}/url`);
      const { url, state } = res.data;
      
      if (!url) {
        toast.error('Failed to get authorization URL');
        setConnecting(null);
        return;
      }
      
      // Store state for validation
      sessionStorage.setItem('oauth_state', state);
      
      // Open OAuth popup
      const width = 600;
      const height = 700;
      const left = (window.screen.width - width) / 2;
      const top = (window.screen.height - height) / 2;
      
      const popup = window.open(
        url,
        `${provider}_oauth`,
        `width=${width},height=${height},top=${top},left=${left},toolbar=no,menubar=no`
      );
      
      if (!popup) {
        toast.error('Popup blocked. Please allow popups for this site.');
        setConnecting(null);
        return;
      }
      
      // Monitor popup closure
      const checkClosed = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkClosed);
          setConnecting(null);
        }
      }, 500);
      
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to initiate connection');
      setConnecting(null);
    }
  };

  // Disconnect account
  const handleDisconnect = async (provider) => {
    if (!confirm(`Disconnect ${PLATFORMS[provider]?.name || provider}? You'll need to reconnect to publish content.`)) {
      return;
    }
    
    try {
      await api.post(`/social/auth/${provider}/disconnect`, {
        user_id: user.id,
      });
      toast.success(`${PLATFORMS[provider]?.name || provider} disconnected`);
      loadAccounts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to disconnect');
    }
  };

  // Refresh token
  const handleRefresh = async (provider) => {
    setRefreshing(provider);
    try {
      await api.post(`/social/auth/${provider}/refresh`, {
        user_id: user.id,
      });
      toast.success('Connection refreshed');
      loadAccounts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Refresh failed. Please reconnect.');
    } finally {
      setRefreshing(null);
    }
  };

  // Format expiration time
  const formatExpiry = (expiresAt) => {
    if (!expiresAt) return null;
    const exp = new Date(expiresAt);
    const now = new Date();
    const diff = exp - now;
    
    if (diff <= 0) return { text: 'Expired', expired: true };
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    if (days > 7) return { text: `Expires in ${days} days`, expired: false };
    if (days > 0) return { text: `Expires in ${days} days`, warning: true };
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    if (hours > 0) return { text: `Expires in ${hours} hours`, warning: true };
    
    return { text: 'Expiring soon', warning: true };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-amber-500 border-t-transparent" />
      </div>
    );
  }

  const connectedCount = Object.keys(accounts).length;

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-8">
      {/* Header */}
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-100 to-rose-100 flex items-center justify-center">
            <Share2 size={24} className="text-amber-600" />
          </div>
          <div>
            <h1 className="text-2xl font-serif text-obsidian">Social Connections</h1>
            <p className="text-muted-foreground">
              Connect your social accounts to auto-publish products and content
            </p>
          </div>
        </div>
        
        {/* Stats */}
        <div className="flex items-center gap-6 pt-4">
          <div className="flex items-center gap-2">
            <Users size={16} className="text-amber-500" />
            <span className="text-sm">
              <strong>{connectedCount}</strong> of {Object.keys(PLATFORMS).length} connected
            </span>
          </div>
          {connectedCount > 0 && (
            <Badge variant="secondary" className="bg-emerald-100 text-emerald-700">
              <CheckCircle2 size={12} className="mr-1" />
              Ready to publish
            </Badge>
          )}
        </div>
      </div>

      {/* Platform Cards */}
      <div className="grid gap-4">
        {Object.entries(PLATFORMS).map(([provider, platform]) => {
          const account = accounts[provider];
          const platformConfig = platforms.find(p => p.provider === provider);
          const isConfigured = platformConfig?.configured !== false;
          const expiry = account ? formatExpiry(account.expires_at) : null;
          const Icon = platform.icon;
          const SecondaryIcon = platform.secondaryIcon;

          return (
            <Card 
              key={provider}
              className={`transition-all duration-300 ${
                account && !account.is_expired 
                  ? 'border-emerald-200 bg-emerald-50/30' 
                  : account?.is_expired 
                    ? 'border-amber-200 bg-amber-50/30'
                    : ''
              }`}
              data-testid={`social-card-${provider}`}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-xl ${platform.color} flex items-center justify-center text-white`}>
                      <Icon size={24} />
                    </div>
                    {SecondaryIcon && (
                      <div className={`w-10 h-10 -ml-5 rounded-xl bg-blue-600 flex items-center justify-center text-white border-2 border-white`}>
                        <SecondaryIcon size={18} />
                      </div>
                    )}
                    <div className={SecondaryIcon ? '-ml-1' : ''}>
                      <CardTitle className="text-lg flex items-center gap-2">
                        {platform.name}
                        {account && !account.is_expired && (
                          <CheckCircle2 size={18} className="text-emerald-500" />
                        )}
                        {account?.is_expired && (
                          <AlertCircle size={18} className="text-amber-500" />
                        )}
                      </CardTitle>
                      <CardDescription className="text-sm mt-0.5">
                        {platform.description}
                      </CardDescription>
                    </div>
                  </div>
                  
                  {/* Connection status & actions */}
                  <div className="flex items-center gap-2">
                    {account ? (
                      <>
                        {expiry?.warning && (
                          <Badge variant="outline" className="text-amber-600 border-amber-300">
                            <Clock size={12} className="mr-1" />
                            {expiry.text}
                          </Badge>
                        )}
                        {account.is_expired ? (
                          <Button
                            size="sm"
                            onClick={() => handleConnect(provider)}
                            disabled={connecting === provider}
                            className="bg-amber-500 hover:bg-amber-600"
                            data-testid={`reconnect-${provider}`}
                          >
                            {connecting === provider ? (
                              <RefreshCw size={14} className="mr-1 animate-spin" />
                            ) : (
                              <Link2 size={14} className="mr-1" />
                            )}
                            Reconnect
                          </Button>
                        ) : (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleRefresh(provider)}
                              disabled={refreshing === provider}
                              data-testid={`refresh-${provider}`}
                            >
                              <RefreshCw size={14} className={refreshing === provider ? 'animate-spin' : ''} />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDisconnect(provider)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              data-testid={`disconnect-${provider}`}
                            >
                              <Unlink size={14} className="mr-1" />
                              Disconnect
                            </Button>
                          </>
                        )}
                      </>
                    ) : (
                      <Button
                        size="sm"
                        onClick={() => handleConnect(provider)}
                        disabled={connecting === provider || !isConfigured}
                        className={!isConfigured ? 'opacity-50' : ''}
                        data-testid={`connect-${provider}`}
                      >
                        {connecting === provider ? (
                          <RefreshCw size={14} className="mr-1 animate-spin" />
                        ) : (
                          <Link2 size={14} className="mr-1" />
                        )}
                        {!isConfigured ? 'Setup Required' : 'Connect'}
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="pt-0">
                {/* Connected account info */}
                {account && (
                  <div className="bg-white/60 rounded-lg p-3 mb-3 border border-border/50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                          <Icon size={16} className={platform.textColor} />
                        </div>
                        <div>
                          <p className="font-medium text-sm">{account.username || 'Connected'}</p>
                          {account.email && (
                            <p className="text-xs text-muted-foreground">{account.email}</p>
                          )}
                        </div>
                      </div>
                      <div className="text-right text-xs text-muted-foreground">
                        <p>Connected {new Date(account.connected_at).toLocaleDateString()}</p>
                        {account.metadata?.instagram_accounts?.length > 0 && (
                          <p className="text-amber-600">
                            + {account.metadata.instagram_accounts.length} Instagram account(s)
                          </p>
                        )}
                        {account.metadata?.facebook_pages?.length > 0 && (
                          <p className="text-blue-600">
                            + {account.metadata.facebook_pages.length} Facebook Page(s)
                          </p>
                        )}
                        {account.metadata?.boards?.length > 0 && (
                          <p className="text-red-600">
                            + {account.metadata.boards.length} Board(s)
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Features */}
                <div className="flex flex-wrap gap-2">
                  {platform.features.map((feature, i) => (
                    <Badge 
                      key={i} 
                      variant="secondary" 
                      className={`text-xs ${account && !account.is_expired ? 'bg-emerald-100 text-emerald-700' : ''}`}
                    >
                      {feature}
                    </Badge>
                  ))}
                </div>
                
                {/* Setup required notice */}
                {!isConfigured && (
                  <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                    <p className="font-medium flex items-center gap-1">
                      <AlertCircle size={14} />
                      API credentials required
                    </p>
                    <p className="text-xs mt-1 text-amber-700">
                      Add {platform.name} OAuth credentials in your environment variables to enable this connection.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Help section */}
      <Card className="bg-gradient-to-br from-slate-50 to-slate-100/50">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Sparkles size={18} className="text-amber-500" />
            Auto-Publish Your Products
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-2">
          <p>
            Once connected, you can automatically share your marketplace products across all your social platforms:
          </p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Share product images on Instagram and Pinterest</li>
            <li>Post product announcements on Facebook and LinkedIn</li>
            <li>Upload product videos to YouTube and TikTok</li>
            <li>Use AI-generated captions and hashtags</li>
          </ul>
          <p className="text-xs pt-2">
            Your credentials are securely stored and tokens are automatically refreshed.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
